import React from 'react';
import { Select, Tag } from 'antd';

// Colours offered for a new tag. Any hex works on the API; these are just the
// swatches the pickers start from, so tags look consistent by default.
export const LEAD_TAG_COLORS = [
  '#1677ff', '#13c2c2', '#52c41a', '#fadb14', '#fa8c16',
  '#f5222d', '#eb2f96', '#722ed1', '#2f54eb', '#8c8c8c',
];

// Next swatch for a new tag, cycling so consecutive new tags differ.
export const nextLeadTagColor = (tags = []) =>
  LEAD_TAG_COLORS[tags.length % LEAD_TAG_COLORS.length];

// One tag as a coloured chip.
export const LeadTagChip = ({ tag, style, ...rest }) => (
  <Tag color={tag?.color} style={{ marginInlineEnd: 4, ...style }} {...rest}>
    {tag?.name}
  </Tag>
);

/**
 * The tags a lead carries, in the user's tag order, with current names and
 * colours. `tagIds` is the lead's `tags`, `details` its `tags_detail` (used
 * until the full list has loaded) and `allTags` the list from /lead/tags/.
 * Once the list has loaded, an id missing from it is a deleted tag.
 */
export const resolveLeadTags = (tagIds = [], details = [], allTags = null) => {
  const byId = new Map((allTags || []).map((tag) => [tag.id, tag]));
  const detailById = new Map((details || []).map((tag) => [tag.id, tag]));
  return (tagIds || [])
    .map((id) => (allTags ? byId.get(id) : detailById.get(id)))
    .filter(Boolean)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
};

/**
 * Multi-select of lead tags showing each as its coloured chip. Used by the
 * lead form and the leads Filter drawer; value is a list of tag ids.
 */
export const LeadTagSelect = ({ tags = [], ...props }) => {
  const byId = new Map(tags.map((tag) => [tag.id, tag]));
  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      optionFilterProp="label"
      style={{ width: '100%' }}
      options={tags.map((tag) => ({ value: tag.id, label: tag.name }))}
      optionRender={(option) => <LeadTagChip tag={byId.get(option.value)} />}
      tagRender={({ value, closable, onClose }) => {
        const tag = byId.get(value);
        if (!tag) return null;
        return (
          <LeadTagChip
            tag={tag}
            closable={closable}
            onClose={onClose}
            onMouseDown={(e) => {
              // Keep the dropdown from toggling when a chip's x is clicked.
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{ marginBlock: 2 }}
          />
        );
      }}
      {...props}
    />
  );
};
